/**
 * useSpeurttochtAdmin Hook
 * Hook for admin management of speurtochts
 */

import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { logActivity } from "@/lib/activity-log";
import {
  Speurtocht,
  SpeurttochtStep,
  SpeurttochtSession,
  SpeurttochtStepCompletion,
  TeamProgress,
  CreateSpeurttochtParams,
  CreateStepParams,
  SpeurttochtStatus,
} from "../types";
import { notifySpeurttochtActivated } from "../utils/notifications";

/** Params for adding a step from admin screen */
interface AddStepParams {
  speurtocht_id: string;
  title: string;
  description?: string;
  step_type: string;
  config: Record<string, unknown>;
  hint?: string;
  lottie_animation?: string;
}

/** Step order update */
interface StepOrderUpdate {
  stepId: string;
  orderIndex: number;
}

interface UseSpeurttochtAdminReturn {
  /** Loading state */
  isLoading: boolean;
  /** Submitting state */
  isSubmitting: boolean;
  /** Error message */
  error: string | null;
  /** Fetch a single speurtocht */
  fetchSpeurtocht: (id: string) => Promise<Speurtocht | null>;
  /** Fetch steps for a speurtocht */
  fetchSteps: (speurttochtId: string) => Promise<SpeurttochtStep[]>;
  /** Fetch a single step */
  fetchStep: (stepId: string) => Promise<SpeurttochtStep | null>;
  /** Fetch sessions for a speurtocht */
  fetchSessions: (speurttochtId: string) => Promise<SpeurttochtSession[]>;
  /** Fetch completions for a session */
  fetchCompletions: (sessionId: string) => Promise<SpeurttochtStepCompletion[]>;
  /** Create a new speurtocht */
  createSpeurtocht: (params: CreateSpeurttochtParams) => Promise<Speurtocht | null>;
  /** Update a speurtocht */
  updateSpeurtocht: (id: string, updates: Partial<Speurtocht>) => Promise<boolean>;
  /** Delete a speurtocht */
  deleteSpeurtocht: (id: string) => Promise<boolean>;
  /** Update speurtocht status */
  updateStatus: (id: string, status: SpeurttochtStatus) => Promise<boolean>;
  /** Add a step */
  addStep: (params: AddStepParams) => Promise<SpeurttochtStep | null>;
  /** Create a step */
  createStep: (params: CreateStepParams) => Promise<SpeurttochtStep | null>;
  /** Update a step */
  updateStep: (id: string, updates: Partial<SpeurttochtStep>) => Promise<boolean>;
  /** Delete a step */
  deleteStep: (id: string) => Promise<boolean>;
  /** Reorder steps */
  reorderSteps: (stepOrders: StepOrderUpdate[]) => Promise<boolean>;
  /** Get all team progress for a speurtocht */
  getTeamProgress: (speurttochtId: string) => Promise<TeamProgress[]>;
  /** Verify a step completion */
  verifyCompletion: (completionId: string, approved: boolean, reason?: string) => Promise<boolean>;
  /** Get all sessions for a speurtocht */
  getSessions: (speurttochtId: string) => Promise<SpeurttochtSession[]>;
}

/**
 * Hook for speurtocht admin operations
 */
export const useSpeurttochtAdmin = (): UseSpeurttochtAdminReturn => {
  const { activeProfile, activeWeekend } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch a single speurtocht
   */
  const fetchSpeurtocht = useCallback(
    async (id: string): Promise<Speurtocht | null> => {
      try {
        const { data, error: fetchError } = await supabase
          .from("speurtochts")
          .select("*")
          .eq("id", id)
          .single();

        if (fetchError) {
          console.error("Error fetching speurtocht:", fetchError);
          return null;
        }

        return data as Speurtocht;
      } catch (err) {
        console.error("Error:", err);
        return null;
      }
    },
    []
  );

  /**
   * Fetch steps for a speurtocht
   */
  const fetchSteps = useCallback(
    async (speurttochtId: string): Promise<SpeurttochtStep[]> => {
      try {
        const { data, error: fetchError } = await supabase
          .from("speurtocht_steps")
          .select("*")
          .eq("speurtocht_id", speurttochtId)
          .order("step_order", { ascending: true });

        if (fetchError) {
          console.error("Error fetching steps:", fetchError);
          return [];
        }

        return data as SpeurttochtStep[];
      } catch (err) {
        console.error("Error:", err);
        return [];
      }
    },
    []
  );

  /**
   * Fetch a single step
   */
  const fetchStep = useCallback(
    async (stepId: string): Promise<SpeurttochtStep | null> => {
      try {
        const { data, error: fetchError } = await supabase
          .from("speurtocht_steps")
          .select("*")
          .eq("id", stepId)
          .single();

        if (fetchError) {
          console.error("Error fetching step:", fetchError);
          return null;
        }

        return data as SpeurttochtStep;
      } catch (err) {
        console.error("Error:", err);
        return null;
      }
    },
    []
  );

  /**
   * Fetch sessions for a speurtocht
   */
  const fetchSessions = useCallback(
    async (speurttochtId: string): Promise<SpeurttochtSession[]> => {
      try {
        const { data, error: fetchError } = await supabase
          .from("speurtocht_sessions")
          .select(`
            *,
            team:team_id(id, name, color, avatar_url),
            started_by_user:started_by(id, full_name, avatar_url)
          `)
          .eq("speurtocht_id", speurttochtId)
          .order("started_at", { ascending: true });

        if (fetchError) {
          console.error("Error fetching sessions:", fetchError);
          return [];
        }

        return data as SpeurttochtSession[];
      } catch (err) {
        console.error("Error:", err);
        return [];
      }
    },
    []
  );

  /**
   * Fetch completions for a session
   */
  const fetchCompletions = useCallback(
    async (sessionId: string): Promise<SpeurttochtStepCompletion[]> => {
      try {
        const { data, error: fetchError } = await supabase
          .from("speurtocht_step_completions")
          .select(`
            *,
            step:step_id(id, title, step_type, step_order),
            completed_by_user:completed_by(id, full_name, avatar_url)
          `)
          .eq("session_id", sessionId)
          .order("completed_at", { ascending: true });

        if (fetchError) {
          console.error("Error fetching completions:", fetchError);
          return [];
        }

        return data as SpeurttochtStepCompletion[];
      } catch (err) {
        console.error("Error:", err);
        return [];
      }
    },
    []
  );

  /**
   * Create a new speurtocht
   */
  const createSpeurtocht = useCallback(
    async (params: CreateSpeurttochtParams): Promise<Speurtocht | null> => {
      if (!activeProfile?.id) {
        setError("Je moet ingelogd zijn");
        return null;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const { data, error: insertError } = await supabase
          .from("speurtochts")
          .insert({
            ...params,
            created_by: activeProfile.id,
          })
          .select()
          .single();

        if (insertError) {
          throw new Error(insertError.message);
        }

        // Log activity
        await logActivity({
          user_id: activeProfile.id,
          action_type: "speurtocht.create",
          action_category: "game",
          target_type: "speurtocht",
          target_id: data.id,
          target_name: data.name,
          weekend_id: activeWeekend?.id,
        });

        return data as Speurtocht;
      } catch (err) {
        console.error("Error creating speurtocht:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij aanmaken speurtocht"
        );
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeProfile?.id, activeWeekend?.id]
  );

  /**
   * Update a speurtocht
   */
  const updateSpeurtocht = useCallback(
    async (id: string, updates: Partial<Speurtocht>): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // First, fetch current speurtocht to check if status is changing to active
        let previousStatus: SpeurttochtStatus | null = null;
        let speurttochtData: Speurtocht | null = null;
        let stepsCount = 0;

        if (updates.status === "active") {
          const { data: spData } = await supabase
            .from("speurtochts")
            .select("*, speurtocht_steps(count)")
            .eq("id", id)
            .single();

          if (spData) {
            const { speurtocht_steps, status, ...rest } = spData as Speurtocht & { speurtocht_steps: { count: number }[] };
            previousStatus = status;
            speurttochtData = { ...rest, status } as Speurtocht;
            stepsCount = speurtocht_steps?.[0]?.count ?? 0;
          }
        }

        const { error: updateError } = await supabase
          .from("speurtochts")
          .update(updates)
          .eq("id", id);

        if (updateError) {
          throw new Error(updateError.message);
        }

        // Send notification if status changed to active
        if (
          updates.status === "active" &&
          previousStatus !== "active" &&
          speurttochtData &&
          activeWeekend?.id &&
          activeProfile
        ) {
          // Use the updated notification config if provided, else use existing
          const config = updates.notification_config || speurttochtData.notification_config;
          
          await notifySpeurttochtActivated({
            speurttochtId: id,
            speurttochtName: updates.name || speurttochtData.name,
            speurttochtIcon: updates.icon || speurttochtData.icon,
            speurttochtColor: updates.color || speurttochtData.color,
            weekendId: activeWeekend.id,
            activatedByUserId: activeProfile.id,
            activatedByUserName: activeProfile.full_name || "Familie Admin",
            stepsCount,
            maxDurationMinutes: updates.max_duration_minutes ?? speurttochtData.max_duration_minutes,
            config,
          });
        }

        // Log activity
        if (activeProfile?.id) {
          await logActivity({
            user_id: activeProfile.id,
            action_type: "speurtocht.update",
            action_category: "game",
            target_type: "speurtocht",
            target_id: id,
            metadata: { updates: Object.keys(updates) },
            weekend_id: activeWeekend?.id,
          });
        }

        return true;
      } catch (err) {
        console.error("Error updating speurtocht:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij bijwerken speurtocht"
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeWeekend?.id, activeProfile]
  );

  /**
   * Delete a speurtocht
   */
  const deleteSpeurtocht = useCallback(
    async (id: string): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Get speurtocht name for logging
        const { data: speurtocht } = await supabase
          .from("speurtochts")
          .select("name")
          .eq("id", id)
          .single();

        const { error: deleteError } = await supabase
          .from("speurtochts")
          .delete()
          .eq("id", id);

        if (deleteError) {
          throw new Error(deleteError.message);
        }

        // Log activity
        if (activeProfile?.id) {
          await logActivity({
            user_id: activeProfile.id,
            action_type: "speurtocht.delete",
            action_category: "game",
            target_type: "speurtocht",
            target_id: id,
            target_name: speurtocht?.name,
            weekend_id: activeWeekend?.id,
          });
        }

        return true;
      } catch (err) {
        console.error("Error deleting speurtocht:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij verwijderen speurtocht"
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeWeekend?.id]
  );

  /**
   * Update speurtocht status
   */
  const updateStatus = useCallback(
    async (id: string, status: SpeurttochtStatus): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // First fetch the speurtocht data for notifications (if activating)
        let speurttochtData: Speurtocht | null = null;
        let stepsCount = 0;
        
        if (status === "active") {
          const { data: spData } = await supabase
            .from("speurtochts")
            .select("*, speurtocht_steps(count)")
            .eq("id", id)
            .single();
          
          if (spData) {
            const { speurtocht_steps, ...rest } = spData as Speurtocht & { speurtocht_steps: { count: number }[] };
            speurttochtData = rest as Speurtocht;
            stepsCount = speurtocht_steps?.[0]?.count ?? 0;
          }
        }

        const { error: updateError } = await supabase
          .from("speurtochts")
          .update({ status })
          .eq("id", id);

        if (updateError) {
          throw new Error(updateError.message);
        }

        // Send notification to all users when speurtocht is activated
        if (status === "active" && speurttochtData && activeWeekend?.id && activeProfile) {
          await notifySpeurttochtActivated({
            speurttochtId: id,
            speurttochtName: speurttochtData.name,
            speurttochtIcon: speurttochtData.icon,
            speurttochtColor: speurttochtData.color,
            weekendId: activeWeekend.id,
            activatedByUserId: activeProfile.id,
            activatedByUserName: activeProfile.full_name || "Familie Admin",
            stepsCount,
            maxDurationMinutes: speurttochtData.max_duration_minutes,
            config: speurttochtData.notification_config,
          });
        }

        // Log activity
        if (activeProfile?.id) {
          await logActivity({
            user_id: activeProfile.id,
            action_type: `speurtocht.${status}` as "speurtocht.draft" | "speurtocht.active" | "speurtocht.paused" | "speurtocht.completed" | "speurtocht.archived",
            action_category: "game",
            target_type: "speurtocht",
            target_id: id,
            metadata: { new_status: status },
            weekend_id: activeWeekend?.id,
          });
        }

        return true;
      } catch (err) {
        console.error("Error updating speurtocht status:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij wijzigen status"
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeWeekend?.id, activeProfile]
  );

  /**
   * Create a step
   */
  const createStep = useCallback(
    async (params: CreateStepParams): Promise<SpeurttochtStep | null> => {
      setIsSubmitting(true);
      setError(null);

      try {
        const { data, error: insertError } = await supabase
          .from("speurtocht_steps")
          .insert(params)
          .select()
          .single();

        if (insertError) {
          throw new Error(insertError.message);
        }

        return data as SpeurttochtStep;
      } catch (err) {
        console.error("Error creating step:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij aanmaken stap"
        );
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  /**
   * Add a step (simplified version for admin screens)
   */
  const addStep = useCallback(
    async (params: AddStepParams): Promise<SpeurttochtStep | null> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Get current max step_order
        const { data: existingSteps } = await supabase
          .from("speurtocht_steps")
          .select("step_order")
          .eq("speurtocht_id", params.speurtocht_id)
          .order("step_order", { ascending: false })
          .limit(1);

        const nextOrder = existingSteps && existingSteps.length > 0 
          ? existingSteps[0].step_order + 1 
          : 0;

        const { data, error: insertError } = await supabase
          .from("speurtocht_steps")
          .insert({
            speurtocht_id: params.speurtocht_id,
            title: params.title,
            description: params.description || null,
            step_type: params.step_type,
            config: params.config,
            step_order: nextOrder,
            hint: params.hint || null,
            lottie_animation: params.lottie_animation || null,
          })
          .select()
          .single();

        if (insertError) {
          throw new Error(insertError.message);
        }

        return data as SpeurttochtStep;
      } catch (err) {
        console.error("Error adding step:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij toevoegen stap"
        );
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  /**
   * Update a step
   */
  const updateStep = useCallback(
    async (id: string, updates: Partial<SpeurttochtStep>): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);

      try {
        const { error: updateError } = await supabase
          .from("speurtocht_steps")
          .update(updates)
          .eq("id", id);

        if (updateError) {
          throw new Error(updateError.message);
        }

        return true;
      } catch (err) {
        console.error("Error updating step:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij bijwerken stap"
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  /**
   * Delete a step
   */
  const deleteStep = useCallback(async (id: string): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from("speurtocht_steps")
        .delete()
        .eq("id", id);

      if (deleteError) {
        throw new Error(deleteError.message);
      }

      return true;
    } catch (err) {
      console.error("Error deleting step:", err);
      setError(
        err instanceof Error ? err.message : "Fout bij verwijderen stap"
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  /**
   * Reorder steps (update step_order for all steps)
   */
  const reorderSteps = useCallback(
    async (stepOrders: StepOrderUpdate[]): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Update each step's order
        const updates = stepOrders.map(({ stepId, orderIndex }) =>
          supabase
            .from("speurtocht_steps")
            .update({ step_order: orderIndex })
            .eq("id", stepId)
        );

        await Promise.all(updates);

        return true;
      } catch (err) {
        console.error("Error reordering steps:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij herordenen stappen"
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  /**
   * Get all team progress for a speurtocht
   */
  const getTeamProgress = useCallback(
    async (speurttochtId: string): Promise<TeamProgress[]> => {
      setIsLoading(true);
      setError(null);

      try {
        // Get all teams for the weekend
        const { data: speurtocht } = await supabase
          .from("speurtochts")
          .select("weekend_id")
          .eq("id", speurttochtId)
          .single();

        if (!speurtocht) {
          throw new Error("Speurtocht niet gevonden");
        }

        const { data: teams, error: teamsError } = await supabase
          .from("teams")
          .select("id, name, color")
          .eq("weekend_id", speurtocht.weekend_id);

        if (teamsError) {
          throw new Error(teamsError.message);
        }

        // Get sessions for this speurtocht
        const { data: sessions, error: sessionsError } = await supabase
          .from("speurtocht_sessions")
          .select(
            `
            *,
            completions:speurtocht_step_completions(
              *,
              step:step_id(id, step_order, title),
              completed_by_user:completed_by(id, full_name)
            )
          `
          )
          .eq("speurtocht_id", speurttochtId);

        if (sessionsError) {
          throw new Error(sessionsError.message);
        }

        // Check which team was first to complete
        const completedSessions = (sessions || [])
          .filter((s) => s.status === "completed")
          .sort(
            (a, b) =>
              new Date(a.completed_at).getTime() -
              new Date(b.completed_at).getTime()
          );
        const firstTeamId = completedSessions[0]?.team_id;

        // Map teams to progress
        const progress: TeamProgress[] = (teams || []).map((team) => {
          const session = sessions?.find((s) => s.team_id === team.id);

          return {
            team_id: team.id,
            team_name: team.name,
            team_color: team.color,
            session_id: session?.id || null,
            status: session?.status || null,
            current_step_index: session?.current_step_index || 0,
            completed_steps: session?.completed_steps || 0,
            total_steps: session?.total_steps || 0,
            total_points: session?.total_points || 0,
            started_at: session?.started_at || null,
            completed_at: session?.completed_at || null,
            duration_seconds: session?.duration_seconds || null,
            is_first_team: team.id === firstTeamId,
            completions:
              session?.completions?.map((c: any) => ({
                step_id: c.step_id,
                step_order: c.step?.step_order || 0,
                completed_by: c.completed_by,
                completed_by_name: c.completed_by_user?.full_name || "Onbekend",
                status: c.status,
                points_earned: c.points_earned,
                completed_at: c.completed_at,
              })) || [],
          };
        });

        // Sort by completion status and progress
        progress.sort((a, b) => {
          if (a.status === "completed" && b.status !== "completed") return -1;
          if (a.status !== "completed" && b.status === "completed") return 1;
          if (a.status === "active" && b.status !== "active") return -1;
          if (a.status !== "active" && b.status === "active") return 1;
          return b.completed_steps - a.completed_steps;
        });

        return progress;
      } catch (err) {
        console.error("Error fetching team progress:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij laden voortgang"
        );
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Verify a step completion
   */
  const verifyCompletion = useCallback(
    async (
      completionId: string,
      approved: boolean,
      reason?: string
    ): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);

      try {
        const { data, error: verifyError } = await supabase.rpc(
          "verify_step_completion",
          {
            p_completion_id: completionId,
            p_approved: approved,
            p_rejection_reason: reason || null,
          }
        );

        if (verifyError) {
          throw new Error(verifyError.message);
        }

        return true;
      } catch (err) {
        console.error("Error verifying completion:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij verifiëren"
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  /**
   * Get all sessions for a speurtocht
   */
  const getSessions = useCallback(
    async (speurttochtId: string): Promise<SpeurttochtSession[]> => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from("speurtocht_sessions")
          .select(
            `
            *,
            team:team_id(id, name, color, avatar_url),
            started_by_user:started_by(id, full_name, avatar_url)
          `
          )
          .eq("speurtocht_id", speurttochtId)
          .order("started_at", { ascending: true });

        if (fetchError) {
          throw new Error(fetchError.message);
        }

        return data as SpeurttochtSession[];
      } catch (err) {
        console.error("Error fetching sessions:", err);
        setError(
          err instanceof Error ? err.message : "Fout bij laden sessies"
        );
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isLoading,
    isSubmitting,
    error,
    fetchSpeurtocht,
    fetchSteps,
    fetchStep,
    fetchSessions,
    fetchCompletions,
    createSpeurtocht,
    updateSpeurtocht,
    deleteSpeurtocht,
    updateStatus,
    addStep,
    createStep,
    updateStep,
    deleteStep,
    reorderSteps,
    getTeamProgress,
    verifyCompletion,
    getSessions,
  };
};

